import * as _ from 'lodash'
import {IBusinessObject} from '../PA.Core/PA.Core.IF.BO';
import {IMessageProvider, IProgressProvider, MessageType} from '../PA.Core/PA.Core.IF.Basic';
import {PAdbContext} from '../PA.Core/PAdbContext';
import {IdentityProvider} from '../PA.Core/Basic/Identity'
import {MessageProvider} from '../PA.Core/Basic/Messages'
import {ProgressProvider} from '../PA.Core/Basic/Progress'
import {
    WB_ClaimTypePaymentTypeRow,
    WB_ClaimTypeRow, WB_PaymentTypeRow
} from "../PA.Core/PAdbSchemaRows";
import {IwbClaimType, IwbPaymentType} from "../PA.Core/PAdbSchemaRowsIF";
import {
    esWB_Claim_Type, esWB_Claim_Type_Payment_Type, esWB_Payment_Type,
} from "../PA.Core/PAdbSchema";
import {ESDataStore} from "../PA.Core/lib/ESDataStore";
import {ESRecord} from "../PA.Core/lib/ESRecord";
import {CreateOptions, ESRecordResolver} from "../PA.Core/lib/ESRecordResolver"
import {ASync} from "../PA.Core/Basic/ASync"

import {PAEnvironment} from "../PA.Core/PAEnvironment";


export interface IClaimTypeLoadOptions {
    dbContext?: PAdbContext
}

const ClaimTypeLoadOptions_default: IClaimTypeLoadOptions = {}

export class ClaimTypeBO implements IBusinessObject {

    //<editor-fold desc = "Properties and Constructor">

    Identity: IdentityProvider;

    CreateOptions: any;

    LoadOptions: any;
    _bIsLoaded: boolean;
    _OnLoadComplete: Promise<IBusinessObject>;
    LoadProgressProvider: IProgressProvider;

    ValidateOptions: any;
    _bIsValidated: boolean;
    ValidateMessageProvider: IMessageProvider;
    ValidateProgressProvider: IProgressProvider;

    SaveOptions: any;
    _bIsSaved: boolean;
    SaveProgressProvider: IProgressProvider;

    DeleteOptions: any;
    _bIsDeleted: boolean;
    DeleteMessageProvider: IMessageProvider;
    DeleteProgressProvider: IProgressProvider;

    dbContext: PAdbContext;
    drWB_ClmType: WB_ClaimTypeRow;

    constructor(options?: any) {

        this.CreateOptions = options || {};

        if ('dbContext' in this.CreateOptions)
            this.dbContext = this.CreateOptions.dbContext;

        this.Identity = new IdentityProvider({id: 0, name: 'New Claim Type'});

        this.LoadOptions = {};
        this._bIsLoaded = false;
        this._OnLoadComplete = undefined;
        this.LoadProgressProvider = new ProgressProvider('Loading', this);

        this.ValidateOptions = {};
        this._bIsValidated = false;
        this.ValidateMessageProvider = new MessageProvider();
        this.ValidateProgressProvider = new ProgressProvider("Validating", this);

        this.SaveOptions = {};
        this._bIsSaved = false;
        this.SaveProgressProvider = new ProgressProvider("Saving", this);

        this.DeleteOptions = {};
        this._bIsDeleted = false;
        this.DeleteMessageProvider = new MessageProvider();
        this.DeleteProgressProvider = new ProgressProvider("Deleting", this);

        this.drWB_ClmType = undefined;
    }

    //</editor-fold>

    get WB_ClaimType(): WB_ClaimTypeRow {
        return this.drWB_ClmType;
    }


    //<editor-fold desc = "IBusinessObject operations">

    LoadNew(options?: object): Promise<IBusinessObject> {
        const self = this;
        this.LoadProgressProvider.start("Creating Claim Type");

        let internalOnComplete = new Promise<IBusinessObject>( (resolve, error) => {

            self.LoadOptions = options || {};
            if('dbContext' in self.LoadOptions)
                self.dbContext = self.LoadOptions.dbContext;
            else
                self.dbContext = new PAdbContext();

            if(this.dbContext == null) {
                var sError = "Unable to determine dbContext for new Claim Type.";
                self.LoadProgressProvider.exception(sError);
                error(sError);
                return;
            }

            // Create record using dbContext
            this.dbContext.createRecordWithKey(esWB_Claim_Type)
                .then((newClaimTypeRow: WB_ClaimTypeRow) => {
                    newClaimTypeRow.setDefaults()
                    self.drWB_ClmType = newClaimTypeRow;
                    self.dbContext.add(esWB_Claim_Type, newClaimTypeRow)
                    self.Identity.setAll(self.WB_ClaimType.cif.WB_CLMTYPE_ID, self.WB_ClaimType.cif.CLAIM_TYPE_ID, self.WB_ClaimType.cif.CLAIM_TYPE_NAME);
                    self.LoadProgressProvider.setName("Loading " + self.Identity.name + "(" + self.Identity.id_ui + ")");
                    self.Identity.listenToRecordUpdates(self.WB_ClaimType, 'WB_CLMTYPE_ID', 'CLAIM_TYPE_ID', 'CLAIM_TYPE_NAME')


                    self.LoadProgressProvider.finish();
                    self._bIsLoaded = true;
                    self._OnLoadComplete = null;
                    resolve(self);

                })
                .catch((err) => {
                    self.LoadProgressProvider.exception(err)
                    error(err)
                })

        });

        self._OnLoadComplete = internalOnComplete

        return  new Promise<IBusinessObject>( (resolve, error) => {
            internalOnComplete
                .then((BO) => {
                    return ASync.resolve()
                }).then((ignored) => {
                self._bIsLoaded = true;
                self._OnLoadComplete = undefined
                self.LoadProgressProvider.finish()
                resolve(self)
            }).catch((err) => {
                self._OnLoadComplete = undefined
                self.LoadProgressProvider.exception(err)
                error(err)
            })
        })
    }

    Load(anId: number | number, options?: object): Promise<IBusinessObject> {

        const self = this;

        let recordResolver: ESRecordResolver
        let recordResolverLoadPromise: Promise<void>

        self.LoadProgressProvider.start("Loading (" + anId + ")");

        self._OnLoadComplete = new Promise<IBusinessObject>((resolve, error) => {

            if (self.bIsLoaded) {
                self.LoadProgressProvider.exception("Claim Type already loaded.")
                resolve(self); // "PaymentType already loaded.";
                return;
            }

            self.LoadOptions = options || {};
            _.defaults(self.LoadOptions, ClaimTypeLoadOptions_default)


            if ('dbContext' in self.LoadOptions)
                self.dbContext = self.LoadOptions.dbContext;
            else if (_.isEmpty(self.dbContext)) {
                self.dbContext = new PAdbContext({});
            }

            if (_.isEmpty(self.dbContext)) {
                error("Unable to determine dbContext for Claim Type.")
                return;
            }

            if('RecordResolver' in self.LoadOptions) {
                recordResolver = (<any> self.LoadOptions).RecordResolver
                recordResolverLoadPromise = ASync.resolve()
            } else {
                recordResolver = ClaimTypeBO.createRecordResolver({dbContext: self.dbContext})
                recordResolverLoadPromise = ClaimTypeBO.setupRecordResolver(recordResolver, [anId])
            }


            recordResolverLoadPromise
                .then(function () {
                    let aClaimTypeRow: any = recordResolver.get(esWB_Claim_Type, anId)

                    if (_.isEmpty(aClaimTypeRow)) {
                        error("Claim Type not Found");
                        return;
                    }

                    self.drWB_ClmType = aClaimTypeRow;
                    self.Identity.setAll(self.WB_ClaimType.cif.WB_CLMTYPE_ID, self.WB_ClaimType.cif.CLAIM_TYPE_ID, self.WB_ClaimType.cif.CLAIM_TYPE_NAME);
                    self.LoadProgressProvider.setName("Loading " + self.Identity.name + "(" + self.Identity.id_ui + ")");
                    self.Identity.listenToRecordUpdates(self.WB_ClaimType, 'WB_CLMTYPE_ID', 'CLAIM_TYPE_ID', 'CLAIM_TYPE_NAME')
                    self._bIsLoaded = true
                    self._OnLoadComplete = undefined
                    self.LoadProgressProvider.finish()
                    resolve(self)
                }).catch(function (err: any) {
                self._OnLoadComplete = undefined
                self.LoadProgressProvider.exception(err)
                error(err)
                return;
            });

        });

        return this._OnLoadComplete;
    }

    get OnLoadComplete(): Promise<IBusinessObject> {
        return this._OnLoadComplete;
    }

    get bIsLoaded(): boolean {
        return this._bIsLoaded;
    }


    Validate(options?: any): Promise<IBusinessObject> {

        const self = this;
        self.ValidateMessageProvider.clearMessages();
        self.ValidateProgressProvider.start("Validating " + self.WB_ClaimType.cif.CLAIM_TYPE_NAME + "(" + self.WB_ClaimType.cif.CLAIM_TYPE_ID + ")");
        return new Promise<IBusinessObject>((resolve, reject) => {
            // Does no validations yet
            self.ValidateOptions = options || {};

            if (self.dbContext == undefined) {
                var sError = "Unable to determine dbContext for to Validate Claim Type.";
                self.ValidateProgressProvider.exception(sError);
                reject(sError);
                return
            }

            Promise.resolve()
                .then((ignore) => {
                    //return  next validation
                    // ()then handler

                    if(self.ValidateMessageProvider.countMessages(MessageType.Error) > 0){
                        self._bIsValidated = false;
                    } else {
                        self._bIsValidated = true;
                    }
                    self.ValidateProgressProvider.finish();
                    resolve(self);

                })
                .catch((err) => {
                    self._bIsValidated = false;
                    self.ValidateProgressProvider.exception(err);
                    resolve(self);

                })


        });
    }



    get bIsValidated(): boolean {
        return this._bIsValidated;
    }


    Save(options?: any): Promise<IBusinessObject> {
        return this.SaveUsing(false, options)
    }

    SaveBatch(options?: any): Promise<IBusinessObject> {
        return this.SaveUsing(true, options)
    }

    async SaveUsing(bBatch: boolean, options?: any): Promise<IBusinessObject> {
        const self = this;

        const wbClmType = <IwbClaimType>self.drWB_ClmType;
        self.SaveProgressProvider.start("Saving " + wbClmType.CLAIM_TYPE_NAME + "(" + wbClmType.CLAIM_TYPE_ID + ")");

        // Doesn't need to do anything yet
        // All the records are in the store, since they were added through find or createNewRecord.
        // -- We could search the tree if the there is any record where store isn't set.
        self._bIsSaved = false;
        try {
            if (self.dbContext == undefined) {
                self._bIsSaved = false;
                let sError = "Unable to determine dbContext to Save Claim Type.";
                self.SaveProgressProvider.exception(sError);
                throw new Error(sError);
                return
            }

            self.SaveOptions = options || {};

            var saveFunction: (esName: string, saveRecords: ESRecord[] | ESRecord, options?: any) => Promise<ESDataStore>
            if (bBatch) {
                saveFunction = self.dbContext.SaveBatch.bind(self.dbContext)
            } else {
                saveFunction = self.dbContext.Save.bind(self.dbContext)
            }

            // Add code to do individual BO save for dbContextRows....
            await self.Validate({})

            if (!self.bIsValidated) {
                self._bIsSaved = false;
                self.SaveProgressProvider.finish();
                return self;
            }


            await saveFunction(esWB_Claim_Type, self.drWB_ClmType, {})
            await saveFunction(esWB_Claim_Type_Payment_Type, self.drWB_ClmType.WB_ClaimTypePaymentTypeRows, {})

            if (bBatch) {
                self.dbContext.addBatchCompleteListener(() => {
                    self._bIsSaved = true
                })
            } else {
                self._bIsSaved = true
            }
            self.SaveProgressProvider.finish();
            return self
        } catch (err) {
            self.SaveProgressProvider.exception(err);
            return self
        }

    }

    get bIsSaved(): boolean {
        return this._bIsSaved;
    }

//Delete
    Delete(options?: any): Promise<IBusinessObject> {
        return this.DeleteUsing(true, options)
    }

    DeleteOnSave(options?: any): Promise<IBusinessObject> {
        return this.DeleteUsing(false, options)
    }

    private async DeleteUsing(bSaveNow: boolean, options?: any): Promise<IBusinessObject> {
        const self = this;

        const wbClmType = <IwbClaimType>self.drWB_ClmType;
        self.DeleteMessageProvider.clearMessages();
        self.DeleteProgressProvider.start("Deleting " + wbClmType.CLAIM_TYPE_NAME + "(" + wbClmType.CLAIM_TYPE_ID + ")");

        try {
            // 1. Validate if we can delete the ClaimType

            // 2. If Valid, mark all the records for deletion.
            self.drWB_ClmType.markDeleted()
            _.forEach(self.WB_ClaimType.WB_ClaimTypePaymentTypeRows, (aRow) => {
                aRow.markDeleted()
            })

            if (bSaveNow) {
                await self.SaveUsing(false)
                self._bIsDeleted = true;
                self.DeleteProgressProvider.finish();
                return self;
            } else {
                self._bIsDeleted = true;
                self.DeleteProgressProvider.finish();
                return self;
            }

        } catch (err) {
            this.DeleteProgressProvider.exception(err)
            return self

        }
    }


    get bIsDeleted(): boolean {
        return this._bIsDeleted;
    }

//</editor-fold>

    //<editor-fold desc = "Payment Types operations">

    async addPaymentType(wbPaymentTypeId: number): Promise<WB_ClaimTypePaymentTypeRow> {
        const self = this

        let returnValue: WB_ClaimTypePaymentTypeRow = undefined

        _.forEach(self.WB_ClaimType.WB_ClaimTypePaymentTypeRows, (aRow) => {
            if (!aRow.isActive)
                return

            if(aRow.cif.WB_PMTTYPE_ID == wbPaymentTypeId){
                returnValue = aRow
                return false
            }

        })

        if(!_.isEmpty(returnValue))
            return returnValue

        let paymentType: WB_PaymentTypeRow  = <any> await self.dbContext.find(esWB_Payment_Type, wbPaymentTypeId)

        if(_.isEmpty(paymentType))
            return undefined

        returnValue = <any> await self.dbContext.createRecordWithKey(esWB_Claim_Type_Payment_Type, {})
        returnValue.cif.WB_CLMTYPE_ID = self.WB_ClaimType.cif.WB_CLMTYPE_ID
        returnValue.WB_ClaimTypeRow = self.WB_ClaimType
        returnValue.cif.WB_PMTTYPE_ID = paymentType.cif.WB_PMTTYPE_ID
        returnValue.WB_PaymentTypeRow = paymentType
        self.dbContext.add(esWB_Claim_Type_Payment_Type, returnValue)
        return returnValue
    }

    async removePaymentType(wbPaymentTypeId: number): Promise<void> {
        const self = this

        _.forEach(self.WB_ClaimType.WB_ClaimTypePaymentTypeRows, (aRow) => {
            if(!aRow.isActive)
                return

            if(aRow.cif.WB_PMTTYPE_ID == wbPaymentTypeId)
                aRow.markDeleted()
        })
    }

    //</editor-fold>

    //<editor-fold desc = "Record Resolver operations <Static>">

    static createRecordResolver(boLoadOptions: any): ESRecordResolver {

        let opts = boLoadOptions || {}
        let dbContext: PAdbContext

        if ('dbContext' in opts) {
            dbContext = opts.dbContext;
        } else {
            dbContext = new PAdbContext({});
        }
        const clm_options: CreateOptions = {
            standardConditions: {
            },
            dctIncludeBelongsToNamedRelations: {
            }
        }

        const aRecResolver = new ESRecordResolver(dbContext, clm_options)

        return aRecResolver;

    }

    static setupRecordResolver(recordResolver: ESRecordResolver, ids: string[] | number[]): Promise<void> {

        return new Promise<void>((resolve, error) => {
            recordResolver.findAllWithRelationships(esWB_Claim_Type,ids).then((result) => {
                resolve()
            }).catch((err) => {
                error(err)
            })
        })
    }

    static concurrentLoadCountHint(): number {
        return 10;
    }

    //</editor-fold>

};

PAEnvironment.registerBusinessObjectClass( ClaimTypeBO , "ClaimTypeBO", "Claim Type")